import type {
  ColDef,
  CellClassParams,
  CellStyle,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { PlanBenefitClickableCellRenderer } from './PlanBenefitClickableCellRenderer'
import { CompareUpdateTooltip } from './CompareUpdateTooltip'
import {
  PLANS_AND_RATES_NON_EDITABLE_COLS,
  GRID_COL_TO_PARAM_FIELD,
  FIELD_CONFIG as PAR_FIELD_CONFIG,
  getOverrideValue,
} from './planAndRateFields'

const PLAN_BENEFITS_IDENTITY_COLS = new Set([
  'year',
  'hiosPlanId',
  'state',
  'benefit',
])

function formatCellValue(params: ValueFormatterParams): string {
  const v = params.value
  if (v === true) return '✓'
  if (v === false) return '✗'
  if (v === null || v === undefined || v === '') return '-'
  if (typeof v === 'number') {
    const key = params.colDef.field ?? ''
    const paramField = GRID_COL_TO_PARAM_FIELD[key] ?? key
    const precision = PAR_FIELD_CONFIG[paramField]?.precision
    if (precision !== undefined) {
      return v.toFixed(precision)
    }
  }
  return String(v)
}

const COLUMN_HEADER_MAP: Record<string, Record<string, string>> = {
  LOCATION: {
    year: 'Year',
    state: 'State',
    county: 'County',
    countyFipsCode: 'FIPS Code',
    zipCode: 'ZIP Code',
    ratingAreaNumeric: 'Rating Area',
  },
  SMOKER_RATES: {
    year: 'Year',
    state: 'State',
    age: 'Age',
    issuerId: 'Issuer ID',
    increaseRate: 'Increase Rate',
    isActive: 'Active',
  },
  SERVICE_AREAS: {
    year: 'Year',
    state: 'State',
    issuerId: 'Issuer ID',
    hiosPlanId: 'HIOS Plan ID',
    serviceAreaId: 'Service Area ID',
    serviceAreaName: 'Service Area Name',
    coverEntireState: 'Cover Entire State',
    countyCode: 'County Code',
    partialCounty: 'Partial County',
    zipcode: 'Zipcode',
    isActive: 'Active',
    sourceName: 'Source Name',
  },
  PLANS_AND_RATES: {
    year: 'Year',
    hiosPlanId: 'HIOS Plan ID',
    state: 'State',
    planMarketingName: 'Plan Name',
    carrier: 'Carrier',
    carrierFriendlyName: 'Carrier Friendly Name',
    planType: 'Plan Type',
    metal: 'Metal Level',
    serviceAreaId: 'Service Area ID',
    isForSale: 'For Sale',
    isHsa: 'HSA',
    onExchange: 'On Exchange',
    offExchange: 'Off Exchange',
    planStatus: 'Plan Status',
    crosswalkHiosId: 'Crosswalk HIOS ID',
    proofOfResidenceRequired: 'Proof of Residence Required',
    tooltip: 'Tooltip',
    actuarialValue: 'Actuarial Value',
    providerNetwork: 'Provider Network',
    baseRate: 'Base Rate',
    planFee: 'Plan Fee',
    ...Object.fromEntries(
      Array.from({ length: 67 }, (_, i) => {
        const n = (i + 1).toString().padStart(2, '0')
        return [`areaFactor${n}`, `Area Factor RA${n}`]
      })
    ),
  },
  PLAN_BENEFITS: {
    year: 'Year',
    hiosPlanId: 'HIOS Plan ID',
    state: 'State',
    benefit: 'Benefit',
    serviceNotCovered: 'Service Not Covered',
    appliesToDeductible: 'Applies To Deductible',
    coinsurance: 'Coinsurance',
    copayAmount: 'Copay Amount',
    copayDayLimit: 'Copay Day Limit',
    coinsuranceCopayOrder: 'Coinsurance Copay Order',
    memberServicePaidCap: 'Member Service Paid Cap',
    coverageVisitLimit: 'Coverage Visit Limit',
    firstDollarVisits: 'First Dollar Visits',
    isGrouped: 'Is Grouped',
    copayAfterFirstVisits: 'Copay After First Visits',
    notes: 'Notes',
    benefitDisplayValue: 'Benefit Display Value',
  },
}

export function fieldValueChanged(
  stagingVal: unknown,
  liveVal: unknown
): boolean {
  return String(stagingVal ?? '') !== String(liveVal ?? '')
}

function toTitleCase(key: string): string {
  const spaced = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
  return spaced.replace(/\b\w/g, (c) => c.toUpperCase())
}

function getColumnTitle(dataType: string, key: string): string {
  return COLUMN_HEADER_MAP[dataType]?.[key] ?? toTitleCase(key)
}

const AMBER_CELL_STYLE = {
  backgroundColor: '#fffbeb',
  color: '#92400e',
  fontWeight: 600,
} as const

const GREEN_CELL_STYLE = {
  backgroundColor: '#f0fdf4',
  color: '#15803d',
  fontWeight: 600,
} as const

const BLUE_CELL_STYLE = {
  backgroundColor: '#dbeafe',
  color: '#1e40af',
  fontWeight: 600,
} as const

export function getCompareReportColDefs(
  dataType: string,
  options?: { onCellClickable?: boolean; sortable?: boolean }
): ColDef[] {
  const headerMap = COLUMN_HEADER_MAP[dataType]
  if (!headerMap) return []
  return Object.keys(headerMap).map((key) => {
    const isClickable =
      options?.onCellClickable &&
      ((dataType === 'PLAN_BENEFITS' &&
        !PLAN_BENEFITS_IDENTITY_COLS.has(key)) ||
        (dataType === 'PLANS_AND_RATES' &&
          !PLANS_AND_RATES_NON_EDITABLE_COLS.has(key)))

    const isSortable = options?.sortable !== false

    const colDef: ColDef = {
      field: key,
      headerName: getColumnTitle(dataType, key),
      sortable: isSortable,
      resizable: true,
      filter: false,
      valueFormatter: formatCellValue,
      ...(isClickable
        ? {
            context: { clickable: true },
            cellRenderer: PlanBenefitClickableCellRenderer,
          }
        : {}),
    }

    colDef.valueGetter = (params) => {
      const overrideParams = params.data?.__overrideParams as
        | Record<string, unknown>
        | null
        | undefined
      if (overrideParams) {
        if (dataType === 'PLANS_AND_RATES') {
          const paramField = GRID_COL_TO_PARAM_FIELD[key] ?? key
          const overrideVal = getOverrideValue(paramField, overrideParams)
          if (overrideVal !== undefined) return overrideVal
        } else if (key in overrideParams) {
          return overrideParams[key]
        }
      }
      const stagingVal = params.data?.[key]
      if (stagingVal !== undefined && stagingVal !== null) return stagingVal
      return (
        (params.data?._currentValue as Record<string, unknown> | undefined)?.[
          key
        ] ?? null
      )
    }

    colDef.cellStyle = (params: CellClassParams): CellStyle | undefined => {
      const overrideParams = params.data?.__overrideParams as
        | Record<string, unknown>
        | null
        | undefined
      let base: CellStyle | undefined
      const overrideStatus = params.data?.__overrideStatus as string | null
      const hasOverride =
        overrideParams &&
        (dataType === 'PLANS_AND_RATES'
          ? getOverrideValue(
              GRID_COL_TO_PARAM_FIELD[key] ?? key,
              overrideParams
            ) !== undefined
          : key in overrideParams)
      if (overrideStatus === 'APPROVED') {
        base = GREEN_CELL_STYLE
      } else if (hasOverride) {
        base = BLUE_CELL_STYLE
      } else if (params.data?.__compareStatus === 'MODIFIED') {
        const liveVal = params.data?._currentValue as
          | Record<string, unknown>
          | undefined
        if (liveVal && key in liveVal) {
          if (fieldValueChanged(params.data[key], liveVal[key])) {
            base = AMBER_CELL_STYLE
          }
        }
      }
      if (isClickable) {
        const approved =
          (params.data as Record<string, unknown>)?.__overrideStatus ===
          'APPROVED'
        return { ...(base ?? {}), cursor: approved ? 'default' : 'pointer' }
      }
      if (
        options?.onCellClickable &&
        (dataType === 'PLAN_BENEFITS' || dataType === 'PLANS_AND_RATES')
      ) {
        return {
          ...(base ?? {}),
          backgroundColor: '#f5f5f5',
          cursor: 'default',
        }
      }
      return base
    }

    colDef.tooltipValueGetter = (params) => {
      const overrideParams = params.data?.__overrideParams as
        | Record<string, unknown>
        | null
        | undefined
      const hasOverride =
        overrideParams &&
        (dataType === 'PLANS_AND_RATES'
          ? getOverrideValue(
              GRID_COL_TO_PARAM_FIELD[key] ?? key,
              overrideParams
            ) !== undefined
          : key in overrideParams)
      if (hasOverride) {
        return '__override__'
      }
      if (params.data?.__compareStatus === 'MODIFIED') {
        const liveVal = params.data?._currentValue as
          | Record<string, unknown>
          | undefined
        if (!liveVal || !(key in liveVal)) return null
        if (!fieldValueChanged(params.data?.[key], liveVal[key])) return null
        return '__changed__'
      }
      return null
    }

    colDef.tooltipComponent = CompareUpdateTooltip

    return colDef
  })
}
